import React, {Fragment} from 'react';
import {Grid} from '@mui/material';
import type {CustomTextProps} from 'schema/types';
import type {Part} from 'schemaDefinition/types';
import {FlexBox, Spacer} from 'components';
import {usePreviewConfigurationContext} from 'schema/preview/contexts';
import {useTextLabel} from 'schema/preview/hooks';
import type {BaseFieldProps} from '../types';
import {useLabelAndPlaceholder} from '../../../form/hooks';

type Props<TPart extends Part> = Pick<
  BaseFieldProps<TPart>,
  'part' | 'layoutDirection'
> & {
  renderValue: () => React.ReactNode | null;
  /** Overrides any label lookup */
  forcedLabel?: string;
  labelProps?: CustomTextProps;
};

export const ValueLayout = <TPart extends Part>({
  renderValue,
  forcedLabel,
  labelProps,
  part,
  layoutDirection,
}: Props<TPart>) => {
  const {label: defaultLabel} = useLabelAndPlaceholder(part);
  const config = usePreviewConfigurationContext();
  const {noLabels, noGrid} = config.layout;

  const TextLabel = useTextLabel(labelProps);

  const labelValue =
    forcedLabel ?? defaultLabel ?? part.name + ': label missing';

  return noLabels && !forcedLabel ? (
    renderValue()
  ) : layoutDirection === 'vertical' ? (
    <Fragment>
      {/* Label */}
      <TextLabel>{labelValue}</TextLabel>
      {/* Value */}
      {renderValue()}
    </Fragment>
  ) : noGrid ? (
    <FlexBox horizontal left>
      {/* Label */}
      <TextLabel>{`${labelValue}:`}</TextLabel>
      <Spacer width={0.5} />
      {/* Value */}
      {renderValue()}
    </FlexBox>
  ) : (
    <Grid container sx={{width: '100%'}} spacing={0}>
      {/* Label */}
      <Grid size={{xs: 4, sm: 3}} sx={{pl: 0}}>
        <TextLabel>{labelValue}</TextLabel>
      </Grid>
      {/* Value */}
      <Grid size={{xs: 8, sm: 9}}>{renderValue()}</Grid>
    </Grid>
  );
};
